// src/components/accueil/RecapAbsence.tsx — SCE Issues
// Le récap « retour de vacances » en cartes (28/10, piste A de la maquette
// https://claude.ai/artifact/TcsuwBJoSyyA3PjMGSUheU, retenue seule) : une
// carte à la fois, segments en haut, « Suivant » en bas. Plein écran au
// téléphone, fenêtre de téléphone centrée au bureau.
//
// Les cartes vides ne s'affichent pas : un opérateur n'a ni « déclarations du
// métier » ni « relais », un retour sans nouveauté de l'appli n'a pas sa carte.
// Le calcul est dans utils/retourAbsence ; ici, la mise en page.

import { useEffect, useRef, useState, type CSSProperties, type JSX, type MouseEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronLeft, ChevronRight, Sparkles, X } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { EtatProbleme } from '@/components/ui/EtatProbleme'
import { Infobulle } from '@/components/ui/Infobulle'
import { PastillePersonne } from '@/components/personnes/PastillePersonne'
import { useClicFond } from '@/hooks/useClicFond'
import { verbeEvenement } from '@/utils/accueil'
import { lienFnct, lienProbleme } from '@/utils/liensFiches'
import { prenom as prenomDe } from '@/utils/noms'
import {
  infobulleEquipement,
  infobulleJour,
  infobulleMetier,
  libelleMetierRecap,
  type CibleEtape,
  type Etape,
  type Mot,
  type Recap,
  type TexteInfobulle,
  type Urgent,
} from '@/utils/retourAbsence'

type IdCarte = 'bienvenue' | 'declarations' | 'relais' | 'vos' | 'attente' | 'suivis' | 'nouveautes' | 'commencer'

const pluriel = (n: number, un: string, plusieurs = `${un}s`): string => (n > 1 ? plusieurs : un)

const FORMAT_MOT = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', weekday: 'long', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })

function dateMot(iso: string): string {
  const t = FORMAT_MOT.format(new Date(iso))
  return t.charAt(0).toUpperCase() + t.slice(1)
}

export function RecapAbsence({
  recap,
  prenom,
  onFermer,
  carteInitiale = 0,
  aCote = false,
  onMarquer,
  onAller,
}: {
  recap: Recap
  prenom: string
  onFermer: () => void
  /** Carte où s'ouvrir (retour d'une fiche). */
  carteInitiale?: number
  /** Rangé à gauche, une fiche ouverte à côté (grand écran) : ni voile, ni clavier. */
  aCote?: boolean
  onMarquer: (id: string, fait: boolean) => void
  /** Ouvre la cible (fiche, page) ; le récap se referme d'abord. */
  onAller: (cible: CibleEtape, carte: number) => void
}): JSX.Element {
  const cartes: IdCarte[] = [
    'bienvenue',
    ...(recap.declarations ? (['declarations'] as const) : []),
    ...(recap.relais ? (['relais'] as const) : []),
    ...(recap.vosDeclarations ? (['vos'] as const) : []),
    ...(recap.attente ? (['attente'] as const) : []),
    ...(recap.suivis ? (['suivis'] as const) : []),
    ...(recap.nouveautes.length ? (['nouveautes'] as const) : []),
    ...(recap.etapes.length ? (['commencer'] as const) : []),
  ]
  const [i, setI] = useState(() => Math.max(0, Math.min(carteInitiale, cartes.length - 1)))
  const carte = cartes[Math.min(i, cartes.length - 1)] ?? 'bienvenue'
  const derniere = i >= cartes.length - 1
  const fond = useClicFond(onFermer)
  const dialogue = useRef<HTMLDivElement>(null)

  useEffect(() => {
    dialogue.current?.focus()
  }, [])

  // À côté d'une fiche : la page décale la fiche vers la droite (globals.css).
  useEffect(() => {
    if (!aCote) return
    document.documentElement.classList.add('recap-a-cote')
    return () => document.documentElement.classList.remove('recap-a-cote')
  }, [aCote])

  // Échap ferme ; ← / → changent de carte (hors champ de saisie : il n'y en a pas).
  // À côté d'une fiche, le clavier est à elle (Échap la ferme, ← / → problème suivant).
  useEffect(() => {
    if (aCote) return
    function auClavier(ev: KeyboardEvent): void {
      if (ev.key === 'Escape') {
        ev.stopImmediatePropagation()
        onFermer()
      } else if (ev.key === 'ArrowRight') setI((x) => Math.min(cartes.length - 1, x + 1))
      else if (ev.key === 'ArrowLeft') setI((x) => Math.max(0, x - 1))
    }
    window.addEventListener('keydown', auClavier, true)
    return () => window.removeEventListener('keydown', auClavier, true)
  }, [onFermer, cartes.length, aCote])

  const aFaire = recap.etapes.find((x) => !x.fait)
  const libelleSuivant = !derniere ? (i === 0 ? 'Voir le récap' : 'Suivant') : carte === 'commencer' && aFaire ? aFaire.action : 'Terminer'
  function suivant(): void {
    if (!derniere) return setI(i + 1)
    if (carte === 'commencer' && aFaire) return onAller(aFaire.cible, i)
    onFermer()
  }
  const aller = (chemin: string): void => onAller({ genre: 'fiche', chemin }, i)

  // Comme une story : un clic sur le tiers gauche revient, ailleurs avance. Un
  // bouton, un lien ou une sélection de texte gardent leur rôle ; la dernière
  // carte ne se ferme pas d'un clic perdu (le bouton jaune le fait).
  function clicCarte(ev: MouseEvent<HTMLDivElement>): void {
    if ((ev.target as HTMLElement).closest('button, a, input, textarea')) return
    if (window.getSelection()?.toString()) return
    const r = ev.currentTarget.getBoundingClientRect()
    if (ev.clientX < r.left + r.width / 3) setI((x) => Math.max(0, x - 1))
    else setI((x) => Math.min(cartes.length - 1, x + 1))
  }

  return createPortal(
    <div className="recap-voile" data-a-cote={aCote} {...(aCote ? {} : fond)} role="presentation">
      {/* Bureau : les flèches de part et d'autre, comme « problème suivant » ; cachées aux bouts (la fenêtre reste centrée). */}
      <button type="button" className="recap-fleche" aria-label="Carte précédente" style={{ visibility: i > 0 ? 'visible' : 'hidden' }} onClick={() => setI(Math.max(0, i - 1))}>
        <ChevronLeft size={26} aria-hidden="true" />
      </button>
      <div ref={dialogue} tabIndex={-1} className="recap-fenetre" role="dialog" aria-modal={!aCote} aria-label="Pendant votre absence">
        <div className="recap-tete">
          <div className="recap-segments">
            {cartes.map((c, k) => (
              <button key={c} type="button" className="recap-segment" aria-label={`Carte ${k + 1} sur ${cartes.length}`} aria-current={k === i ? 'step' : undefined} onClick={() => setI(k)}>
                <span data-fait={k <= i} />
              </button>
            ))}
          </div>
          <div className="recap-barre">
            <span className="recap-surtitre" style={{ flex: 1 }}>Pendant votre absence · {i + 1} / {cartes.length}</span>
            {!derniere && <button type="button" className="recap-lien" style={{ padding: '12px 8px' }} onClick={() => setI(cartes.length - 1)}>Passer</button>}
            <button type="button" className="recap-rond" aria-label="Fermer le récap" onClick={onFermer}>
              <X size={22} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* key : chaque carte rejoue ses entrées. */}
        <div key={carte} className="recap-corps" onClick={clicCarte}>
          {carte === 'bienvenue' && <CarteBienvenue recap={recap} prenom={prenom} nbCartes={cartes.length - 1} />}
          {carte === 'declarations' && <CarteDeclarations recap={recap} />}
          {carte === 'relais' && <CarteRelais recap={recap} onAller={aller} />}
          {carte === 'vos' && <CarteVos recap={recap} onAller={aller} />}
          {carte === 'attente' && <CarteAttente recap={recap} onAller={aller} />}
          {carte === 'suivis' && <CarteSuivis recap={recap} onAller={aller} />}
          {carte === 'nouveautes' && <CarteNouveautes recap={recap} />}
          {carte === 'commencer' && <CarteCommencer etapes={recap.etapes} onMarquer={onMarquer} />}
        </div>

        <div className="recap-pied">
          {i > 0 && (
            <button type="button" className="recap-rond recap-rond-cadre" aria-label="Carte précédente" onClick={() => setI(i - 1)}>
              <ChevronLeft size={22} aria-hidden="true" />
            </button>
          )}
          <div style={{ flex: 1 }}>
            <Button variant="primary" size="lg" fullWidth onClick={suivant}>{libelleSuivant}</Button>
          </div>
        </div>
      </div>
      <button type="button" className="recap-fleche" aria-label="Carte suivante" style={{ visibility: derniere ? 'hidden' : 'visible' }} onClick={() => setI(Math.min(cartes.length - 1, i + 1))}>
        <ChevronRight size={26} aria-hidden="true" />
      </button>
    </div>,
    document.body,
  )
}

// ─── Cartes ───

function Titre({ children }: { children: ReactNode }): JSX.Element {
  return <h2 className="recap-entre recap-titre">{children}</h2>
}

function CarteBienvenue({ recap, prenom, nbCartes }: { recap: Recap; prenom: string; nbCartes: number }): JSX.Element {
  const p = recap.periode
  return (
    <div className="flex flex-col" style={{ gap: 22, paddingTop: 20 }}>
      <h2 className="recap-entre" style={{ margin: 0, fontSize: 40, lineHeight: '44px', fontWeight: 500, letterSpacing: '-0.01em' }}>
        Bon retour{prenom ? `, ${prenom}` : ''}.
      </h2>
      <p className="recap-entre-2" style={{ margin: 0, fontSize: 20, lineHeight: '28px', fontWeight: 300 }}>
        Votre dernière visite date du {p.derniereVisite}. {recap.phrase}
      </p>
      {p.calendrier.length > 0 && (
        <div className="recap-entre-3 flex flex-col" style={{ gap: 10, marginTop: 4 }}>
          <div className="recap-calendrier" aria-hidden="true">
            {p.calendrier.map((c, k) => (
              <div key={c.jour ?? `vide-${k}`} className="recap-case-jour" data-weekend={c.weekend} data-vide={!c.jour}>
                {c.jour && (
                  <>
                    <span style={{ fontSize: 11, lineHeight: '13px' }}>{c.lettre}</span>
                    <span style={{ fontSize: 16, lineHeight: '18px', fontWeight: 500 }}>{c.numero}</span>
                  </>
                )}
              </div>
            ))}
          </div>
          <span className="recap-secondaire">{p.libelle}</span>
        </div>
      )}
      {nbCartes > 0 && (
        <p className="recap-entre-4" style={{ margin: 0, fontSize: 16, lineHeight: '22px' }}>
          L'essentiel tient en {nbCartes > 1 ? `${nbCartes} cartes` : 'une carte'}. Une minute, pas plus.
        </p>
      )}
    </div>
  )
}

function CarteDeclarations({ recap }: { recap: Recap }): JSX.Element | null {
  const d = recap.declarations
  if (!d) return null
  const max = Math.max(1, ...d.parJour.map((j) => j.total))
  const jourLePlusCharge = d.jourMax ? d.parJour.find((j) => j.total === d.jourMax?.n) : undefined
  const habitude = d.habitude && d.moyenne !== null
    ? `${d.habitude === 'plus' ? 'Un peu plus que d\'habitude' : d.habitude === 'moins' ? 'Un peu moins que d\'habitude' : 'Comme d\'habitude'} : ${d.moyenne} sur une période comme celle-ci, en moyenne.`
    : ''
  return (
    <div className="flex flex-col" style={{ gap: 14, paddingTop: 12 }}>
      <div className="recap-entre flex items-baseline" style={{ gap: 12 }}>
        <span className="recap-grand-nombre">{d.total}</span>
        <span style={{ fontSize: 21, lineHeight: '26px', fontWeight: 500 }}>
          {pluriel(d.total, 'problème')}<br />{pluriel(d.total, 'déclaré', 'déclarés')}
        </span>
      </div>
      <p className="recap-entre-2" style={{ margin: 0, fontSize: 16, lineHeight: '22px' }}>
        {recap.surPerimetre}.{habitude ? ` ${habitude}` : ''}
      </p>
      <div className="recap-entre-3 recap-cadre flex flex-col" style={{ gap: 8, padding: '16px 14px 12px' }}>
        {/* Survol (bureau) : le jour, par métier, et les problèmes déclarés ce jour-là. */}
        <div className="recap-barres" aria-hidden="true">
          {d.parJour.map((j, k) => (
            <BulleRecap key={j.jour} texte={infobulleJour(j)} className="recap-colonne">
              {j.total === max && max > 1 && <span style={{ fontSize: 12, lineHeight: '14px', fontWeight: 700, textAlign: 'center' }}>{j.total}</span>}
              <span className="recap-pousse flex flex-col-reverse" style={{ gap: 2, animationDelay: `${k * 0.03}s` }}>
                {j.parMetier.map((x) => (
                  <span key={x.metier} style={{ display: 'block', height: Math.round((x.n / max) * 112), borderRadius: 3, background: `var(--color-metier-${x.metier.toLowerCase()})` }} />
                ))}
              </span>
              <span style={{ display: 'block', height: 2, background: 'var(--color-gray-20)' }} />
            </BulleRecap>
          ))}
        </div>
        <div className="flex" style={{ gap: 4 }} aria-hidden="true">
          {d.parJour.map((j) => (
            <span key={j.jour} style={{ flex: 1, textAlign: 'center', fontSize: 11, lineHeight: '13px', color: 'var(--color-text-secondary)' }}>{d.parJour.length <= 21 ? j.lettre : ''}</span>
          ))}
        </div>
        <div className="flex flex-wrap" style={{ gap: '6px 16px', paddingTop: 6, fontSize: 14, lineHeight: '17px' }}>
          {d.metiers.map((x) => (
            <BulleRecap key={x.metier} texte={infobulleMetier(x)} className="recap-survol inline-flex items-center" style={{ gap: 6 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: `var(--color-metier-${x.metier.toLowerCase()})` }} />
              {libelleMetierRecap(x.metier)} {x.n}
            </BulleRecap>
          ))}
        </div>
      </div>
      <div className="recap-entre-4 flex flex-col" style={{ gap: 10, marginTop: 4, fontSize: 16, lineHeight: '22px' }}>
        {d.jourMax && (
          <span>
            Le jour le plus chargé :{' '}
            {jourLePlusCharge ? (
              <BulleRecap texte={infobulleJour(jourLePlusCharge)} className="recap-survol"><strong style={{ fontWeight: 500 }}>{d.jourMax.libelle}</strong></BulleRecap>
            ) : <strong style={{ fontWeight: 500 }}>{d.jourMax.libelle}</strong>}
            , {d.jourMax.n} déclarations.
          </span>
        )}
        {d.equipementMax && (
          <span>
            L'équipement le plus cité :{' '}
            <BulleRecap texte={infobulleEquipement(d.equipementMax)} className="recap-survol"><strong style={{ fontWeight: 500 }}>{d.equipementMax.numeroSerie}</strong></BulleRecap>
            , avec {d.equipementMax.n} problèmes.
          </span>
        )}
      </div>
    </div>
  )
}

/** L'infobulle de l'appli (bureau seulement), avec les textes calculés par utils/retourAbsence. */
function BulleRecap({ texte, className, style, children }: { texte: TexteInfobulle; className?: string; style?: CSSProperties; children: ReactNode }): JSX.Element {
  return (
    <Infobulle titre={texte.titre} {...(texte.detail ? { detail: texte.detail } : {})} lignes={texte.lignes} largeur={320} className={className} style={style}>
      {children}
    </Infobulle>
  )
}

function LigneChiffre({ n, children, dernier }: { n: number; children: ReactNode; dernier?: boolean }): JSX.Element {
  return (
    <div className="flex items-center" style={{ gap: 14, padding: 14, borderBottom: dernier ? undefined : '1px solid var(--color-gray-10)' }}>
      <span style={{ width: 56, flexShrink: 0, fontSize: 34, lineHeight: '36px', fontWeight: 300, textAlign: 'right' }}>{n}</span>
      <div className="flex flex-col" style={{ flex: 1, gap: 6, fontSize: 16, lineHeight: '20px' }}>{children}</div>
    </div>
  )
}

function BlocMot({ mot, onAller }: { mot: Mot; onAller: (chemin: string) => void }): JSX.Element {
  return (
    <button type="button" className="recap-entre-3 recap-mot" onClick={() => onAller(lienProbleme(mot.problemId))}>
      <span className="recap-surtitre">Un mot pour vous · {mot.problemId}</span>
      <span style={{ fontSize: 16, lineHeight: '22px' }}>« {mot.texte} »</span>
      <span className="recap-secondaire" style={{ fontSize: 12, lineHeight: '16px' }}>{mot.auteur} · {dateMot(mot.date)}</span>
    </button>
  )
}

function CarteRelais({ recap, onAller }: { recap: Recap; onAller: (chemin: string) => void }): JSX.Element | null {
  const r = recap.relais
  if (!r) return null
  const principal = r.personnes[0]
  const tient = principal?.coequipier
  const autres = tient ? r.personnes.slice(1) : r.personnes
  const lignes = [
    r.qualifies > 0 ? 'qualifies' : null,
    r.clos > 0 ? 'clos' : null,
    r.envois > 0 ? 'envois' : null,
  ].filter((x): x is 'qualifies' | 'clos' | 'envois' => !!x)
  return (
    <div className="flex flex-col" style={{ gap: 18, paddingTop: 16 }}>
      {tient && principal ? (
        <div className="recap-entre flex items-center" style={{ gap: 14 }}>
          <PastillePersonne nom={principal.nom} upn={principal.upn} taille={64} fond="var(--color-gray-90)" taillePolice={22} />
          <div className="flex flex-col" style={{ gap: 2, minWidth: 0 }}>
            <span style={{ fontSize: 25, lineHeight: '28px', fontWeight: 500 }}>{prenomDe(principal.nom, principal.upn) || principal.nom} a tenu la boutique</span>
            <span className="recap-secondaire">{principal.nom}{principal.role ? ` · ${principal.role}` : ''}</span>
          </div>
        </div>
      ) : (
        <Titre>Qui a pris le relais</Titre>
      )}
      {lignes.length > 0 && (
        <div className="recap-entre-2 recap-cadre flex flex-col" style={{ overflow: 'hidden' }}>
          {lignes.map((l, k) => {
            const dernier = k === lignes.length - 1
            if (l === 'qualifies') return <LigneChiffre key={l} n={r.qualifies} dernier={dernier}>{pluriel(r.qualifies, 'problème qualifié', 'problèmes qualifiés')}</LigneChiffre>
            if (l === 'clos') {
              return (
                <LigneChiffre key={l} n={r.clos} dernier={dernier}>
                  <span>{pluriel(r.clos, 'problème clos', 'problèmes clos')}</span>
                  {r.badges.length > 0 && (
                    <span className="flex flex-wrap" style={{ gap: 6 }}>
                      {r.badges.map((b) => <Badge key={b.badge} badgeCloture={b.badge} libelle={`${b.badge} × ${b.n}`} />)}
                    </span>
                  )}
                </LigneChiffre>
              )
            }
            return <LigneChiffre key={l} n={r.envois} dernier={dernier}>{pluriel(r.envois, 'envoi au BE', 'envois au BE')}</LigneChiffre>
          })}
        </div>
      )}
      {autres.length > 0 && (
        <div className="recap-entre-3 flex flex-col" style={{ gap: 8 }}>
          {tient && <span className="recap-surtitre">Et aussi</span>}
          {autres.slice(0, 4).map((p) => (
            <div key={p.upn ?? p.nom} className="flex items-center" style={{ gap: 10, fontSize: 14, lineHeight: '18px' }}>
              <PastillePersonne nom={p.nom} upn={p.upn} taille={28} fond="var(--color-gray-10)" couleur="var(--color-black)" />
              <span style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontWeight: 500 }}>{p.nom}</strong>
                <span className="recap-secondaire"> · {[p.qualifies ? `${p.qualifies} qualifié${p.qualifies > 1 ? 's' : ''}` : '', p.clos ? `${p.clos} clos` : '', p.envois ? `${p.envois} envoi${p.envois > 1 ? 's' : ''} au BE` : ''].filter(Boolean).join(', ')}</span>
              </span>
            </div>
          ))}
        </div>
      )}
      {r.mot && <BlocMot mot={r.mot} onAller={onAller} />}
    </div>
  )
}

function CarteVos({ recap, onAller }: { recap: Recap; onAller: (chemin: string) => void }): JSX.Element | null {
  const v = recap.vosDeclarations
  if (!v) return null
  const morceaux = [
    v.qualifiees ? `${v.qualifiees} ${pluriel(v.qualifiees, 'qualifié', 'qualifiés')}` : '',
    v.closes ? `${v.closes} clos` : '',
    v.annulees ? `${v.annulees} ${pluriel(v.annulees, 'annulé', 'annulés')}` : '',
    v.commentaires ? `${v.commentaires} ${pluriel(v.commentaires, 'commentaire')}` : '',
  ].filter(Boolean)
  return (
    <div className="flex flex-col" style={{ gap: 16, paddingTop: 16 }}>
      <Titre>Sur vos déclarations</Titre>
      <p className="recap-entre-2" style={{ margin: 0, fontSize: 16, lineHeight: '22px' }}>
        {v.problemes} de vos problèmes {v.problemes > 1 ? 'ont' : 'a'} bougé{morceaux.length ? ` : ${morceaux.join(', ')}` : ''}.
      </p>
      <div className="recap-entre-3 recap-cadre flex flex-col" style={{ overflow: 'hidden' }}>
        {v.lignes.map(({ evenement, issue }) => (
          <button key={issue.id} type="button" className="recap-ligne" onClick={() => onAller(lienProbleme(issue.id))}>
            <EtatProbleme statut={issue.statut} criticite={issue.criticite} />
            <span className="flex flex-col" style={{ flex: 1, minWidth: 0, gap: 2 }}>
              <span className="recap-ellipse" style={{ fontSize: 15, lineHeight: '19px' }}>
                <strong style={{ fontWeight: 500 }}>{evenement.auteur || 'Quelqu\'un'}</strong> {verbeEvenement(evenement, false)} {issue.id}
              </span>
              <span className="recap-ellipse recap-secondaire" style={{ fontSize: 13, lineHeight: '17px' }}>{issue.titre}</span>
            </span>
          </button>
        ))}
      </div>
      {v.mot && <BlocMot mot={v.mot} onAller={onAller} />}
    </div>
  )
}

function Tuile({ n, libelle, precision, alerte }: { n: number; libelle: string; precision?: string; alerte?: boolean }): JSX.Element {
  return (
    <div className="recap-cadre flex flex-col" style={{ gap: 2, padding: 14 }}>
      <span style={{ fontSize: 34, lineHeight: '36px', fontWeight: 300, color: alerte && n > 0 ? 'var(--color-traffic-red)' : undefined }}>{n}</span>
      <span style={{ fontSize: 14, lineHeight: '17px' }}>{libelle}</span>
      {precision && <span className="recap-secondaire" style={{ fontSize: 12, lineHeight: '16px' }}>{precision}</span>}
    </div>
  )
}

function LigneUrgent({ u, onAller }: { u: Urgent; onAller: (chemin: string) => void }): JSX.Element {
  if (u.genre === 'fnct') {
    const a = u.action
    return (
      <button type="button" className="recap-ligne" onClick={() => onAller(lienFnct(a.fnct.numero))}>
        <span className="recap-pastille-fnct" aria-hidden="true">FNC-T</span>
        <span className="flex flex-col" style={{ flex: 1, minWidth: 0, gap: 2 }}>
          <span lang="en" className="recap-ellipse" style={{ fontSize: 15, lineHeight: '19px', fontWeight: 500 }}>{a.action.description}</span>
          <span className="recap-secondaire" style={{ fontSize: 13, lineHeight: '17px' }}>
            {a.fnct.numero} · <span style={{ color: 'var(--color-traffic-red)' }}>{a.retard} j de retard</span>
          </span>
        </span>
      </button>
    )
  }
  const i = u.issue
  return (
    <button type="button" className="recap-ligne" onClick={() => onAller(lienProbleme(i.id))}>
      <EtatProbleme statut={i.statut} criticite={i.criticite} />
      <span className="flex flex-col" style={{ flex: 1, minWidth: 0, gap: 2 }}>
        <span className="recap-ellipse" style={{ fontSize: 15, lineHeight: '19px', fontWeight: 500 }}>{i.titre}</span>
        <span className="recap-ellipse recap-secondaire" style={{ fontSize: 13, lineHeight: '17px' }}>
          {[i.id, i.numeroSerie, libelleMetierRecap(i.metier)].filter(Boolean).join(' · ')}
        </span>
      </span>
    </button>
  )
}

function CarteAttente({ recap, onAller }: { recap: Recap; onAller: (chemin: string) => void }): JSX.Element | null {
  const a = recap.attente
  if (!a) return null
  const operateur = recap.role === 'operateur'
  const tuiles: JSX.Element[] = operateur
    ? [
        <Tuile key="encours" n={a.enCours} libelle={`${pluriel(a.enCours, 'déclaration')} en cours`} />,
        <Tuile key="aq" n={a.aQualifier} libelle="encore à qualifier" />,
      ]
    : [
        <Tuile key="aq" n={a.aQualifier} libelle="à qualifier" precision={a.aQualifierVieux ? `${a.aQualifierVieux} de plus de 7 j` : a.arrives ? `+${a.arrives} pendant l'absence` : undefined} />,
        <Tuile key="crit" n={a.critiques} libelle={`${pluriel(a.critiques, 'critique')} en cours`} alerte />,
      ]
  if (a.actionsRetard > 0) tuiles.push(<Tuile key="fnct" n={a.actionsRetard} libelle={`${pluriel(a.actionsRetard, 'action FNC-T', 'actions FNC-T')} en retard`} alerte />)
  return (
    <div className="flex flex-col" style={{ gap: 16, paddingTop: 16 }}>
      <Titre>Ce qui vous attend</Titre>
      <div className="recap-entre-2 recap-tuiles">{tuiles}</div>
      {a.urgents.length > 0 && (
        <>
          <span className="recap-entre-3 recap-surtitre">Les plus urgents</span>
          <div className="recap-entre-3 recap-cadre flex flex-col" style={{ overflow: 'hidden' }}>
            {a.urgents.map((u) => <LigneUrgent key={u.genre === 'fnct' ? u.action.action.id : u.issue.id} u={u} onAller={onAller} />)}
          </div>
        </>
      )}
    </div>
  )
}

function CarteSuivis({ recap, onAller }: { recap: Recap; onAller: (chemin: string) => void }): JSX.Element | null {
  const s = recap.suivis
  if (!s) return null
  return (
    <div className="flex flex-col" style={{ gap: 16, paddingTop: 16 }}>
      <Titre>Sur ce que vous suivez</Titre>
      <p className="recap-entre-2" style={{ margin: 0, fontSize: 16, lineHeight: '22px', color: 'var(--color-text-secondary)' }}>
        Vos cloches ont sonné {s.sonneries} fois. Le détail est dans « Éléments suivis ».
      </p>
      <div className="recap-entre-3 recap-cadre flex flex-col" style={{ overflow: 'hidden' }}>
        {s.lignes.map(({ issue, n }) => (
          <button key={issue.id} type="button" className="recap-ligne" onClick={() => onAller(lienProbleme(issue.id))}>
            <EtatProbleme statut={issue.statut} criticite={issue.criticite} />
            <span className="flex flex-col" style={{ flex: 1, minWidth: 0, gap: 2 }}>
              <span className="recap-ellipse" style={{ fontSize: 15, lineHeight: '19px', fontWeight: 500 }}>{issue.titre}</span>
              <span className="recap-ellipse recap-secondaire" style={{ fontSize: 13, lineHeight: '17px' }}>
                {[issue.id, issue.numeroSerie, `${n} ${pluriel(n, 'nouveauté')}`].filter(Boolean).join(' · ')}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

function CarteNouveautes({ recap }: { recap: Recap }): JSX.Element {
  const n = recap.nouveautes.length
  return (
    <div className="flex flex-col" style={{ gap: 16, paddingTop: 16 }}>
      <Titre>L'appli a changé aussi</Titre>
      <p className="recap-entre-2" style={{ margin: 0, fontSize: 16, lineHeight: '22px', color: 'var(--color-text-secondary)' }}>
        {n > 1 ? `${n} nouveautés sorties` : 'Une nouveauté sortie'} pendant votre absence.
      </p>
      <div className="recap-entre-3 flex flex-col" style={{ gap: 10 }}>
        {recap.nouveautes.map((x) => (
          <div key={`${x.le}-${x.titre}`} className="recap-cadre flex" style={{ gap: 12, padding: '12px 14px' }}>
            <Sparkles size={20} aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }} />
            <span className="flex flex-col" style={{ gap: 3 }}>
              <span style={{ fontSize: 15, lineHeight: '19px', fontWeight: 500 }}>{x.titre}</span>
              <span className="recap-secondaire" style={{ fontSize: 14, lineHeight: '18px' }}>{x.detail}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function CarteCommencer({ etapes, onMarquer }: { etapes: readonly Etape[]; onMarquer: (id: string, fait: boolean) => void }): JSX.Element {
  const reste = etapes.filter((x) => !x.fait).length
  return (
    <div className="flex flex-col" style={{ gap: 16, paddingTop: 16 }}>
      <Titre>Par quoi commencer</Titre>
      <p className="recap-entre-2" style={{ margin: 0, fontSize: 16, lineHeight: '22px', color: 'var(--color-text-secondary)' }}>
        {reste === 0
          ? 'Tout est fait. Le récap ne vous sera plus proposé.'
          : `${etapes.length > 1 ? `${etapes.length === 2 ? 'Deux' : etapes.length === 3 ? 'Trois' : 'Quatre'} choses, dans cet ordre` : 'Une chose d\'abord'}. Cochez à mesure.`}
      </p>
      <div className="recap-entre-3 flex flex-col" style={{ gap: 10 }}>
        {etapes.map((x, k) => (
          <button key={x.id} type="button" className="recap-etape" aria-pressed={x.fait} onClick={() => onMarquer(x.id, !x.fait)}>
            {x.fait ? (
              <span className="recap-etape-rond" data-fait="true"><Check size={16} strokeWidth={3.4} aria-hidden="true" /></span>
            ) : (
              <span className="recap-etape-rond">{k + 1}</span>
            )}
            <span className="flex flex-col" style={{ flex: 1, minWidth: 0, gap: 3 }}>
              <span style={{ fontSize: 16, lineHeight: '20px', fontWeight: 500, textDecoration: x.fait ? 'line-through' : undefined }}>{x.titre}</span>
              <span className="recap-secondaire" style={{ fontSize: 14, lineHeight: '17px', overflowWrap: 'anywhere' }}>{x.detail}</span>
            </span>
          </button>
        ))}
      </div>
      <span className="recap-entre-4 recap-secondaire" style={{ fontSize: 14, lineHeight: '18px' }}>
        Le récap reste trois jours dans le menu de votre compte, si vous voulez y revenir.
      </span>
    </div>
  )
}
